import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Menu, X, Home, Settings, Save, Sun, Moon, 
  CircleDot, Grid3X3, FileUp, ChevronLeft, ArrowRightLeft, Calculator, Code, ClipboardList 
} from 'lucide-react';

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/turning', icon: CircleDot, label: 'Turning' },
    { path: '/milling', icon: Grid3X3, label: 'Milling' },
    { path: '/cad-import', icon: FileUp, label: 'CAD Import' },
    { path: '/converter', icon: ArrowRightLeft, label: 'Converter' },
    { path: '/feeds-speeds', icon: Calculator, label: 'Feeds/Speeds' },
    { path: '/cycle-generator', icon: Code, label: 'Cycles' },
    { path: '/job-plans', icon: ClipboardList, label: 'Job Plans' },
    { path: '/saved', icon: Save, label: 'Programs' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const bottomNavItems = [
    navItems[0], // Home
    navItems[1], // Turning
    navItems[2], // Milling
    navItems[3], // CAD Import
    navItems.find(i => i.path === '/job-plans'), // Job Plans
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-industrial-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-industrial-surface border-b border-industrial-border z-30 flex items-center px-4 safe-area-top">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-industrial-surface-highlight transition-colors"
          data-testid="menu-toggle-btn"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6 text-industrial-text" />
        </button>
        
        <div className="flex-1 text-center">
          <h1 className="font-heading font-bold text-lg tracking-wide text-industrial-safety">
            G-CODE PRO
          </h1>
        </div>

        <button
          onClick={toggleTheme}
          className="p-2 -mr-2 rounded-lg hover:bg-industrial-surface-highlight transition-colors"
          data-testid="theme-toggle-btn"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5 text-industrial-safety" />
          ) : (
            <Moon className="w-5 h-5 text-industrial-readout" />
          )}
        </button>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed left-0 top-0 h-full w-72 bg-industrial-surface border-r border-industrial-border z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-industrial-border safe-area-top">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-industrial-safety to-orange-600 rounded flex items-center justify-center">
                <Grid3X3 className="w-5 h-5 text-black" />
              </div>
              <span className="font-heading font-bold text-industrial-safety">G-CODE PRO</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-industrial-surface-highlight transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-industrial-text-secondary" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <button
                    onClick={() => handleNav(item.path)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all min-h-[48px] ${
                      isActive(item.path)
                        ? 'bg-industrial-safety/20 text-industrial-safety border-l-4 border-industrial-safety'
                        : 'text-industrial-text hover:bg-industrial-surface-highlight'
                    }`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-industrial-border">
            <div className="flex items-center justify-between text-sm text-industrial-text-secondary">
              <span>Version 1.0</span>
              <span className="text-industrial-readout">FANUC • Siemens • Haas</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-14 pb-20 min-h-screen">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-industrial-surface border-t border-industrial-border z-30 safe-area-bottom">
        <div className="flex items-center justify-around h-full max-w-lg mx-auto">
          {bottomNavItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-[56px] transition-colors ${
                isActive(item.path)
                  ? 'text-industrial-safety'
                  : 'text-industrial-text-secondary'
              }`}
            >
              <item.icon className={`w-5 h-5 ${isActive(item.path) ? 'drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]' : ''}`} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
